/**
 * AI engine selector — the wiring that makes the V2 engine reachable.
 *
 * Two engines produce the same result cards, so nothing downstream changes:
 *
 *   V1 (default) — the existing pipeline.
 *   V2           — Research → Memory → Thinking → Library → Art Director
 *                  → Improvement Loop → OpenAI → Design JSON V2
 *                  → Rendering Engine → Editor Integration.
 *
 * Select with REACT_APP_AI_ENGINE=v2. V1 stays available until V2 is verified.
 *
 * This module is the app-layer bridge and the ONLY place the two engines meet:
 * `designV2/` never imports V1, and V1 never imports `designV2/`. It composes
 * the published facade of each V2 phase and adds no logic of its own.
 */

import { buildGenerationBrief, generateTemplates } from './openai';
import { composeResearchBrief } from './designV2/research';
import { DesignMemory } from './designV2/memory';
import { getGenome, GENOMES } from './designV2/genome';
import { think } from './designV2/thinking';
import { selectPatterns } from './designV2/library';
import { reviewCreativePlan } from './designV2/artDirector';
import { runImprovementLoop } from './designV2/improvement';
import { runGeneration, conceptDivergence } from './designV2/runtime';

export const ENGINES = Object.freeze(['v1', 'v2']);

/**
 * The engine this build runs.
 *
 * V2 is the product: OpenAI designs the document, the Art Director judges it,
 * and the renderer resolves it. V1 remains reachable with
 * REACT_APP_AI_ENGINE=v1 purely as a rollback — it is not a fallback, and
 * nothing selects it automatically.
 */
export const activeEngine = () => (process.env.REACT_APP_AI_ENGINE === 'v1' ? 'v1' : 'v2');

/** Patterns the Thinking Engine would keep: on-language and mutually compatible. */
const coherentPatterns = (criteria, genomeId, limit = 4) => selectPatterns(criteria)
  .map((r) => r.pattern)
  .filter((p) => p.idealGenomes.includes(genomeId))
  .reduce((kept, p) => (kept.some((k) => k.incompatiblePatterns.includes(p.id) || p.incompatiblePatterns.includes(k.id)) ? kept : [...kept, p]), [])
  .slice(0, limit);

/** A result card, in the shape the results screen already renders. */
const cardFrom = (session, review, plan, brief, index) => {
  const pages = session.editorState.pages;
  const { width, height } = session.editorState.canvasSize;

  // Preview and editor now show the SAME pages, because the images on them are
  // real. They diverged only while the preview was faking imagery the editor
  // document did not have: the runtime resolves the designer's asset intents to
  // actual photographs before anything renders, so an operator previews and
  // then edits the same design, and the picture they approved is the picture
  // they keep.
  return {
    id: session.id,
    name: brief.documentType || 'Template',
    documentType: brief.documentType || '—',
    category: brief.category || brief.industry || '—',
    concept: plan.creativeDirection?.choice || `Concept ${index + 1}`,
    dnaSummary: `${session.theme.genome || 'design'} · ${plan.visualLanguage?.choice || ''}`.trim(),
    // The Art Director's judgement of the FINISHED DOCUMENT is this engine's
    // quality signal — not the plan review, which was a gate before design.
    qualityScore: review.scores.creative,
    publishable: review.approved,
    quality: {
      qualityScore: review.scores.creative,
      publishable: review.approved,
      // Shaped for the existing results screen: it renders `errors[].message`
      // and `improvementSuggestions[]` and is not modified for V2.
      errors: review.weaknesses.map(({ message, detects, severity }) => ({ message, detects, severity })),
      improvementSuggestions: review.revisionRequests.map((r) => r.request),
      scores: review.scores,
      strengths: review.strengths,
      review,
    },
    // Persisted and editable, carrying the resolved photographs. The image is a
    // normal editable element: an operator can swap it like any other.
    editorDocument: { canvas_width: width, canvas_height: height, pages },
    page: pages[0],
    imagesResolved: pages
      .flatMap((pg) => pg.elements || [])
      .filter((el) => el && el.designV2 && el.designV2.resolvedAsset).length,
    canvasSize: session.editorState.canvasSize,
    session,
  };
};

/**
 * Run the V2 engine for the wizard selections.
 *
 * Mirrors `generateTemplates` (V1): same arguments, same `{ cards, stats }`.
 * @returns {Promise<{ cards: Object[], stats: Object }>}
 */
/**
 * Repairs allowed for this run, or `undefined` to use the runtime default.
 *
 * Each repair is a second, THIRD-PARTY-BILLED inference request: the critic
 * rejecting a design is what triggers one, so a single "generate" can cost up
 * to three calls. `REACT_APP_AI_MAX_REPAIRS=0` makes a run cost exactly one,
 * which is what a controlled acceptance test needs — the design is then shown
 * with whatever verdict the critic gave it, rejected included.
 *
 * It changes no default: unset, this returns undefined and the runtime's own
 * DEFAULT_MAX_REPAIRS applies exactly as before. The critic, its thresholds and
 * the repair machinery are all untouched — this only decides how many times the
 * loop is allowed to ASK the model again.
 */
export const configuredMaxRepairs = () => {
  const raw = process.env.REACT_APP_AI_MAX_REPAIRS;
  if (raw === undefined || raw === null || `${raw}`.trim() === '') return undefined;
  const n = Number(raw);
  return Number.isInteger(n) && n >= 0 ? n : undefined;
};

export const generateTemplatesV2 = async (selections, count = 1, { signal, provider, brand, settings, maxRepairs } = {}) => {
  const brief = buildGenerationBrief(selections, { brand, settings });

  // Research takes the taxonomy ids (its knowledge is keyed by slug); the brief
  // carries the display names the model reads.
  const research = composeResearchBrief({
    industry: selections.industry,
    category: selections.category,
    purpose: selections.purpose,
    audience: selections.audience,
  });

  const genome = getGenome(research.recommendedGenomes[0]) || GENOMES[0];

  // Memory: nothing is persisted yet, so this yields no constraints today. The
  // stage is real and wired; a durable store drops in behind MemoryStore.
  const memory = new DesignMemory();
  const { constraints } = memory.check({
    documentType: brief.documentType, industry: selections.industry,
    purpose: selections.purpose, genome: genome.id,
  });

  const plan = think({ brief: { ...brief, ...selections }, research, genome, constraints });
  const patterns = coherentPatterns(
    { documentType: brief.documentType, industry: selections.industry, genome: genome.id },
    genome.id,
  );

  // The Art Director reviews; the loop revises only what it asks for.
  const improved = await runImprovementLoop({ plan, research, genome, patterns, constraints });

  // Concepts are designed one at a time, each against what this run already
  // produced. The runtime owns the three gates (schema, editor, art director)
  // and the bounded repair between them.
  const run = await runGeneration(
    {
      plan: improved.plan,
      research,
      genome,
      patterns: improved.patterns,
      constraints,
      // Brand identity and generation preferences reach the prompt through
      // here. The runtime passes `inputs` straight to buildDesignRequest; no
      // stage between them interprets it.
      brief,
      baseDescriptor: {
        concept: improved.plan.creativeDirection?.choice,
        composition: improved.plan.compositionStrategy?.choice,
        genome: genome.id,
      },
    },
    {
      count,
      provider,
      signal,
      // Explicit argument wins; otherwise the environment; otherwise the
      // runtime default. Passing undefined leaves the default in place.
      maxRepairs: maxRepairs === undefined ? configuredMaxRepairs() : maxRepairs,
      sessionOptions: { name: brief.documentType, context: { categoryName: brief.category } },
    },
  );

  const cards = run.concepts.map((c, i) => cardFrom(c.session, c.review, improved.plan, brief, i));

  if (cards.length === 0) {
    // A rejection must say WHAT was wrong. These are the same corrections the
    // runtime already sent back to the model, so the message names the real cause.
    const detail = run.rejected
      .map((r) => (r.corrections || []).map((c) => `${c.path ? `${c.path}: ` : ''}${c.message}`).join('; '))
      .filter(Boolean)
      .join(' | ');
    const error = new Error(
      `OpenAI could not produce a usable design (${run.rejected.length} rejected after repair attempts). ${detail}`,
    );
    error.validationReports = run.rejected;
    throw error;
  }

  const divergence = conceptDivergence(run.concepts);

  return {
    cards,
    stats: {
      engine: 'v2',
      requested: count,
      produced: cards.length,
      rejected: run.rejected,
      repairsUsed: run.stats.repairsUsed,
      approvedOnMerit: run.stats.approvedOnMerit,
      // Evidence the concepts actually differ, not just an assertion that they do.
      conceptDivergence: divergence,
      genome: genome.id,
      approved: improved.approved,
      stopReason: improved.stopReason,
      patterns: improved.patterns.map((p) => p.id),
    },
  };
};

export const generateWithActiveEngine = (selections, count, options) => (
  activeEngine() === 'v2'
    ? generateTemplatesV2(selections, count, options)
    : generateTemplates(selections, count, options)
);
